// Store-free on purpose: downloads/common.ts drags in ~/store (browser-only under bun test),
// so the pure label helpers live here where unit tests can import them.
import { SITE_DATA_LAYER_ID_PREFIX } from '@civilgrid/utils/src/extract/constants';
import type { ProjectCustomData } from '~/api/projectCustomData';
import { layerIdToDownloadLayer, layerIdToDownloadLayerOverrides } from '~/store2/downloadLayers';
import { TOCLegendParentMap } from '~/store2/TOCLayers';
import { CUSTOM_DATA_LAYER_PREFIX } from '../map/customDataLayers.helpers';
import { getSiteDataLayerLabel } from './extract/siteDataLayers';
import { CUSTOM_DATA_LAYER_FALLBACK_LABEL } from './layerLabels';

// Declared in layerLabels.ts (the store-free leaf both history views share); re-exported so the
// common.ts barrel keeps its import surface.
export { CUSTOM_DATA_LAYER_FALLBACK_LABEL } from './layerLabels';

export interface GetLayerLabelOptions {
  customDataById?: Record<string, ProjectCustomData>;
}

// Resolve one stored `layers` entry to a picker id. Extract submissions store the picker's ids
// directly, while legacy ESRI rows store the GPServer name, so both shapes have to resolve.
function getLayerId(layerId: string): string | undefined {
  if (layerIdToDownloadLayer[layerId]) return layerId;

  return Object.keys(layerIdToDownloadLayer).find((k) => {
    if (layerIdToDownloadLayerOverrides[k] === layerId) return k;
    return layerIdToDownloadLayer[k] === layerId;
  });
}

/**
 * Human label for one entry of a download record's `layers`. Custom-data entries
 * (`customData:<id>`) resolve against the project's custom-data rows and NEVER render raw —
 * missing rows get the generic fallback. Site-data entries (`siteData:*`) resolve against the
 * catalog, same no-raw rule. Everything else keeps the legacy ESRI reverse-lookup
 * (unknown names return undefined and are dropped by the caller, e.g. 'Project Outline').
 */
export function getLayerLabel(layerId: string, options: GetLayerLabelOptions = {}): string | undefined {
  if (layerId.startsWith(CUSTOM_DATA_LAYER_PREFIX)) {
    const id = layerId.slice(CUSTOM_DATA_LAYER_PREFIX.length);
    return options.customDataById?.[id]?.label ?? CUSTOM_DATA_LAYER_FALLBACK_LABEL;
  }

  if (layerId.startsWith(SITE_DATA_LAYER_ID_PREFIX)) return getSiteDataLayerLabel(layerId);

  const foundLayer = getLayerId(layerId);
  if (!foundLayer) return undefined;

  const label = TOCLegendParentMap.get(foundLayer);
  let finalLabel = label || layerId;
  if (layerId.toLowerCase().includes('digitized')) finalLabel += ' (Digitized)';
  if (layerId === 'Parcels_Production') finalLabel = 'Land Rights / Parcels';
  return finalLabel;
}
